import { Dispatch, SetStateAction } from 'react'
import { AnyMemoized, Memoized } from './dependencies'
import { useCallback, useState } from '.'
import getLocalStorage from 'src/helpers/getLocalStorage'
import getSessionStorage from 'src/helpers/getSessionStorage'

type SetValueHandler<S> = Memoized<Dispatch<SetStateAction<S | undefined>>>

// The original usePersistedState has issues where it deletes existing data on load and may also do additional
// re-renders. A new version has been created so we can gradually roll it out without risking breaking existing code.
const usePersistedStateV2 = <S extends AnyMemoized>(
  key: string,
  defaultValue: S,
  isSessionOnly?: true,
): [S, SetValueHandler<S>] => {
  const storage = isSessionOnly ? getSessionStorage() : getLocalStorage()

  const [value, setValue] = useState<S | undefined>(() => {
    const possibleValue = storage?.getItem(key) ?? undefined
    if (possibleValue !== undefined) {
      return JSON.parse(possibleValue)
    }
  })

  const updateStorage = useCallback(
    newValue => {
      if (!storage) return
      if (newValue !== undefined) {
        storage.setItem(key, JSON.stringify(newValue))
      } else {
        storage.removeItem(key)
      }
    },
    [storage, key],
  )

  const setValueHandler = useCallback(
    (nextStateHandler: SetStateAction<S | undefined>) => {
      // In React 19, we can just update the local storage using the callback in the second argument of useState
      if (nextStateHandler instanceof Function) {
        setValue((prevState: S | undefined) => {
          const nextState = nextStateHandler(prevState)
          updateStorage(nextState)
          return nextState
        })
      } else {
        setValue(nextStateHandler)
        updateStorage(nextStateHandler)
      }
    },
    [setValue, updateStorage],
  )

  return [value ?? defaultValue, setValueHandler]
}

export default usePersistedStateV2
